import { usersOGService } from '../../services';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../useReduxToolkit';

/**
 * The user's clock might have drifted a few minutes from standard wall clock time.
 * This happens if the user's PC is not configured to sync with network time servers.
 * It causes weird bugs like 'patient waiting for -30 seconds'.
 *
 * Our solution is to query our server once for the current time,
 * and save the difference in our Redux store.
 *
 * We currently do not factor the network request duration,
 * so the accuracy is likely to be within a few seconds, which is acceptable.
 *
 * @returns drift in milliseconds between user's time and our server's time.
 */
export const useClockDrift = (): number => {
  const [clockDrift, setClockDrift] = useState<number | undefined>(undefined);
  const dispatch = useAppDispatch();

  useEffect(() => {
    let isMounted = true;

    if (clockDrift === undefined) {
      dispatch(usersOGService.endpoints.getClockDrift.initiate())
        .unwrap()
        .then((data) => {
          if (isMounted) {
            setClockDrift(data);
          }

          return undefined;
        })
        .catch(() => undefined);
    }

    return () => {
      isMounted = false;
    };
  }, [clockDrift, dispatch]);

  return clockDrift ?? 0;
};
